import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Divider,
	Grid,
	Typography,
	InputAdornment,
	IconButton,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { validatePaymentLinkAction } from "../../Actions/PlanManagementActions";
import CustomSnackbar from "../../Sections/Common/snackBar";
import {
	makePlanPaymentLinkAction,
	makeRenewPlanPaymentAction,
} from "../../Actions/TenantActions";
import {
	CreditCard as CreditCardIcon,
	Visibility as EyeIcon,
	VisibilityOff as EyeOffIcon,
	Shield as ShieldIcon,
	Info as InfoIcon,
	CheckCircle as CheckIcon,
	Lock as LockIcon,
	Verified as VerifiedIcon,
} from "@mui/icons-material";
import moment from "moment";
import {
	MyCalendarIcon,
	MyCreditCardIcon,
	MyLockIcon,
	MyMoneyIcon,
	MyShieldIcon,
	MyStarIcon,
	MyCheckIcon,
} from "../../Assets/svg/MyIcons";
import { makePlanPaymentAction } from "../../Actions/PaymentAction";
import { useCallback } from "react";

const PlanPaymentPage = () => {
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [loading, setLoading] = useState(false);
	const [isValidating, setIsValidating] = useState(true);
	const [planData, setPlanData] = useState({});
	const [isLinkExpired, setIsLinkExpired] = useState(false);
	const [cardNumber, setCardNumber] = useState("");
	const [cardHolder, setCardHolder] = useState("");
	const [expiryDate, setExpiryDate] = useState("");
	const [cvv, setCvv] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [address, setAddress] = useState("");
	const [showCvv, setShowCvv] = useState(false);
	const [errors, setErrors] = useState({});
	const [cardType, setCardType] = useState(null);

	const queryParams = new URLSearchParams(window.location.search);
	const data = queryParams.get("data");

	const validatePaymentLink = useCallback(async () => {
		try {
			setIsValidating(true);
			const response = await validatePaymentLinkAction({ data });
			const planDetails = response?.data?.data || {};
			setPlanData(planDetails);
		} catch (error) {
			if (
				error?.response?.data?.status === 404 ||
				error?.response?.data?.isError
			) {
				setIsLinkExpired(true);
			} else {
				setSnackbar({
					open: true,
					message:
						error.response.data.message ||
						"Something went wrong opening link",
					severity: "error",
				});
			}
		} finally {
			setIsValidating(false);
		}
	}, [data]);

	useEffect(() => {
		if (data) {
			validatePaymentLink();
		}
	}, [data, validatePaymentLink]);

	const handlePayment = async () => {
		try {
			setLoading(true);

			let amountToPay = planData?.amountToPay || 0;
			if(planData?.currencyDetails?.symbol !== "$" && planData?.isCustomPlan === true) {
				amountToPay = Number(Number(planData?.amountToPay) / Number(planData?.currencyDetails?.exchangeRate)).toFixed(2);
			}
			// console.log(planData)
			
			let cardPayment = {
				cardNumber,
				cardExpMonthYear: expiryDate,
				cvv,
				amount: amountToPay,
				zipCode,
				address,
				cardHolderName: cardHolder,
				email: planData.email,
				billingCycle: planData.billingCycle,
				tenantDetails: {
					id: planData.id,
					domainUrl: planData.domainUrl,
					companyName: planData.companyName,
					companyAddress: planData.companyAddress,
					companyPhoneNumber: planData.companyPhoneNumber,
					companyEmail: planData.companyEmail,
				},
				planId: planData.planId || null,
			};

			const paymentResponse = await makePlanPaymentAction(cardPayment);
			const { transactionId, response, mediaFileUrl } =
				paymentResponse?.data;

			planData.transactionId = transactionId;
			planData.paymentResponse = response;
			planData.mediaFileUrl = mediaFileUrl;
			let tenantResponse;
			let changedPlan = planData;
			if(paymentResponse?.data?.paymentMode){
				changedPlan.paymentMode = paymentResponse.data.paymentMode;
			}
			changedPlan.amountToPay = amountToPay;
			if (planData?.mode && planData?.mode === "plan_payment_link") {
				tenantResponse = await makeRenewPlanPaymentAction({
					data: changedPlan,
				});
			} else {
				tenantResponse = await makePlanPaymentLinkAction({
					data: changedPlan,
				});
			}
			const { message = null } = tenantResponse?.data;
			setSnackbar({
				open: true,
				message: message || "Payment done",
				severity: "success",
			});
			setTimeout(() => {
				window.location.href = "https://" + planData.domainUrl;
			}, 1000);
			setLoading(false);
		} catch (error) {
			setSnackbar({
				open: true,
				message:
					error.response.data.message ||
					"Something went wrong while payment",
				severity: "error",
			});
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 500);
		}
	};

	// Format card number with spaces
	const formatCardNumber = (value) => {
		const cleaned = value.replace(/\s/g, "");
		const match = cleaned.match(/.{1,4}/g);
		return match ? match.join(" ") : cleaned;
	};

	// Format expiry date as MM/YY
	const formatExpiryDate = (value) => {
		const cleaned = value.replace(/\D/g, "");
		if (cleaned.length >= 2) {
			return cleaned.slice(0, 2) + "/" + cleaned.slice(2, 4);
		}
		return cleaned;
	};

	// Detect card type
	const detectCardType = (number) => {
		const cleaned = number.replace(/\s/g, "");
		if (/^4/.test(cleaned)) return "visa";
		if (/^5[1-5]/.test(cleaned)) return "mastercard";
		if (/^3[47]/.test(cleaned)) return "amex";
		if (/^6/.test(cleaned)) return "discover";
		return null;
	};

	// Get card type display info
	const getCardTypeDisplay = () => {
		const types = {
			visa: { name: "Visa", color: "#1e40af" },
			mastercard: { name: "Mastercard", color: "#D32B2B" },
			amex: { name: "Amex", color: "#16a34a" },
			discover: { name: "Discover", color: "#f97316" },
		};
		return types[cardType] || null;
	};

	// Get card gradient
	const getCardGradient = () => {
		if (!cardType)
			return "linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)";

		const gradients = {
			visa: "linear-gradient(135deg, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)",
			mastercard:
				"linear-gradient(135deg, #ea580c 0%, #dc2626 50%, #b91c1c 100%)",
			amex: "linear-gradient(135deg, #16a34a 0%, #15803d 50%, #166534 100%)",
			discover:
				"linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)",
		};
		return (
			gradients[cardType] ||
			"linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)"
		);
	};

	// Validation functions
	const validateCardNumber = (value) => {
		const cleaned = value.replace(/\s/g, "");
		if (!cleaned) return "Card number is required";
		if (cardType === "amex" && cleaned.length !== 15) {
			return "American Express cards must have 15 digits";
		}
		if (cardType !== "amex" && cleaned.length !== 16) {
			return "Card number must have 16 digits";
		}
		// Luhn algorithm check
		let sum = 0;
		let isEven = false;
		for (let i = cleaned.length - 1; i >= 0; i--) {
			let digit = parseInt(cleaned[i]);
			if (isEven) {
				digit *= 2;
				if (digit > 9) digit -= 9;
			}
			sum += digit;
			isEven = !isEven;
		}
		if (sum % 10 !== 0) return "Invalid card number";
		return "";
	};

	const validateCardHolder = (value) => {
		if (!value.trim()) return "Card holder name is required";
		if (value.trim().length < 2)
			return "Name must be at least 2 characters";
		return "";
	};

	const validateExpiryDate = (value) => {
		if (!value) return "Expiry date is required";
		const cleaned = value.replace(/\D/g, "");
		if (cleaned.length !== 4) return "Invalid expiry date";
		const month = parseInt(cleaned.slice(0, 2));
		const year = parseInt("20" + cleaned.slice(2, 4));
		if (month < 1 || month > 12) return "Invalid month";
		const expiry = moment(
			`${year}-${month.toString().padStart(2, "0")}-01`,
		);
		if (expiry.isBefore(moment(), "month")) return "Card has expired";
		return "";
	};

	const validateCVV = (value) => {
		if (!value) return "CVV is required";
		const maxLength = cardType === "amex" ? 4 : 3;
		if (value.length !== maxLength) {
			return cardType === "amex"
				? "CVV must be 4 digits"
				: "CVV must be 3 digits";
		}
		return "";
	};

	const validateZipCode = (value) => {
		if (!value.trim()) return "Zip code is required";
		const cleaned = value.trim().replace(/\D/g, "");
		if (cleaned.length !== 6) return "Zip code must be 6 digits";
		return "";
	};

	const validateAddress = (value) => {
		if (!value.trim()) return "Address is required";
		if (value.trim().length < 5)
			return "Address must be at least 5 characters";
		return "";
	};
	// Handle card number change with type detection
	const handleCardNumberChange = (e) => {
		const formatted = formatCardNumber(e.target.value);
		const cleaned = formatted.replace(/\s/g, "");
		const maxLength = cardType === "amex" ? 15 : 16;

		if (cleaned.length <= maxLength) {
			setCardNumber(formatted);
			const detectedType = detectCardType(cleaned);
			setCardType(detectedType);
			if (errors.cardNumber) {
				setErrors({ ...errors, cardNumber: "" });
			}
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	if (isValidating) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<CircularProgress color="#344d95" />
			</div>
		);
	}

	if (isLinkExpired) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					background:
						"linear-gradient(135deg, #f8fafc 0%, #e3f2fd 30%, #f3e5f5 100%)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 2,
					position: "relative",
					overflow: "hidden",
					fontFamily: "var(--app-font)",
				}}
			>
				{/* Animated Background Elements */}
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						overflow: "hidden",
						pointerEvents: "none",
					}}
				>
					<Box
						sx={{
							position: "absolute",
							width: "350px",
							height: "350px",
							background:
								"linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(156, 39, 176, 0.1))",
							borderRadius: "50%",
							top: "-100px",
							left: "-100px",
							filter: "blur(80px)",
							animation: "float1 6s ease-in-out infinite",
							"@keyframes float1": {
								"0%, 100%": {
									transform: "translateY(0px) rotate(0deg)",
								},
								"50%": {
									transform: "translateY(-20px) rotate(5deg)",
								},
							},
						}}
					/>
					<Box
						sx={{
							position: "absolute",
							width: "300px",
							height: "300px",
							background:
								"linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(63, 81, 181, 0.1))",
							borderRadius: "50%",
							bottom: "-80px",
							right: "-80px",
							filter: "blur(80px)",
							animation: "float2 8s ease-in-out infinite",
							"@keyframes float2": {
								"0%, 100%": {
									transform: "translateY(0px) rotate(0deg)",
								},
								"50%": {
									transform: "translateY(20px) rotate(-5deg)",
								},
							},
						}}
					/>
					<Box
						sx={{
							position: "absolute",
							width: "200px",
							height: "200px",
							background:
								"linear-gradient(135deg, rgba(63, 81, 181, 0.08), rgba(25, 118, 210, 0.08))",
							borderRadius: "50%",
							top: "30%",
							right: "20%",
							filter: "blur(60px)",
							animation: "float3 10s ease-in-out infinite",
							"@keyframes float3": {
								"0%, 100%": {
									transform: "translateY(0px) rotate(0deg)",
								},
								"50%": {
									transform: "translateY(-15px) rotate(3deg)",
								},
							},
						}}
					/>
				</Box>
				<Container
					maxWidth="sm"
					sx={{ position: "relative", zIndex: 10 }}
				>
					<Card
						sx={{
							maxWidth: "450px",
							mx: "auto",
							borderRadius: "24px",
							background: "rgba(255, 255, 255, 0.95)",
							backdropFilter: "blur(10px)",
							boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
							border: "1px solid rgba(255, 255, 255, 0.2)",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								background:
									"linear-gradient(135deg, #9c27b0 0%, #3f51b5 100%)",
								p: 4,
								textAlign: "center",
								color: "white",
								position: "relative",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									top: 15,
									right: 15,
									background: "rgba(255, 255, 255, 0.2)",
									borderRadius: "50%",
									width: 40,
									height: 40,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<MyLockIcon color="#fff" size={20} />
							</Box>
							<Typography
								variant="h4"
								sx={{ fontWeight: 700, mb: 1 }}
								color="white"
							>
								Payment Link Expired
							</Typography>
							<Typography
								variant="body2"
								sx={{ color: "rgba(255,255,255,0.8)" }}
							>
								This payment link is no longer valid. Please
								contact support or request a new link.
							</Typography>
						</Box>
						<CardContent sx={{ textAlign: "center", py: 6 }}>
							<Box sx={{ mb: 3 }}>
								<Chip
									icon={<MyLockIcon />}
									label="Link Expired"
									variant="outlined"
									sx={{
										backgroundColor:
											"rgba(244, 67, 54, 0.1)",
										color: "#f44336",
										borderColor: "rgba(244, 67, 54, 0.2)",
										fontWeight: 500,
										fontSize: "16px",
									}}
								/>
							</Box>
							<Typography
								variant="body1"
								sx={{ color: "text.secondary", mb: 3 }}
							>
								If you believe this is a mistake, please reach
								out to our support team for assistance.
							</Typography>
							<Button
								variant="contained"
								color="primary"
								href="/"
								sx={{
									py: 2,
									borderRadius: "16px",
									textTransform: "none",
									fontSize: "16px",
									fontWeight: 600,
									boxShadow:
										"0 8px 25px rgba(25, 118, 210, 0.3)",
									mt: 2,
								}}
							>
								Go to Home
							</Button>
						</CardContent>
					</Card>
				</Container>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background:
					"linear-gradient(135deg, #f8fafc 0%, #e3f2fd 30%, #f3e5f5 100%)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				p: 2,
				position: "relative",
				overflow: "hidden",
				fontFamily: "var(--app-font)",
			}}
		>
			{/* Animated Background Elements */}
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					overflow: "hidden",
					pointerEvents: "none",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						width: "350px",
						height: "350px",
						background:
							"linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(156, 39, 176, 0.1))",
						borderRadius: "50%",
						top: "-100px",
						left: "-100px",
						filter: "blur(80px)",
						animation: "float1 6s ease-in-out infinite",
						"@keyframes float1": {
							"0%, 100%": {
								transform: "translateY(0px) rotate(0deg)",
							},
							"50%": {
								transform: "translateY(-20px) rotate(5deg)",
							},
						},
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						width: "300px",
						height: "300px",
						background:
							"linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(63, 81, 181, 0.1))",
						borderRadius: "50%",
						bottom: "-80px",
						right: "-80px",
						filter: "blur(80px)",
						animation: "float2 8s ease-in-out infinite",
						"@keyframes float2": {
							"0%, 100%": {
								transform: "translateY(0px) rotate(0deg)",
							},
							"50%": {
								transform: "translateY(20px) rotate(-5deg)",
							},
						},
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						width: "200px",
						height: "200px",
						background:
							"linear-gradient(135deg, rgba(63, 81, 181, 0.08), rgba(25, 118, 210, 0.08))",
						borderRadius: "50%",
						top: "30%",
						right: "20%",
						filter: "blur(60px)",
						animation: "float3 10s ease-in-out infinite",
						"@keyframes float3": {
							"0%, 100%": {
								transform: "translateY(0px) rotate(0deg)",
							},
							"50%": {
								transform: "translateY(-15px) rotate(3deg)",
							},
						},
					}}
				/>
			</Box>

			<Grid container sx={{ position: "relative", zIndex: 10 }}>
				{loading ? (
					<Card
						sx={{
							maxWidth: "450px",
							mx: "auto",
							borderRadius: "24px",
							background: "rgba(255, 255, 255, 0.95)",
							backdropFilter: "blur(10px)",
							boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
							border: "1px solid rgba(255, 255, 255, 0.2)",
						}}
					>
						<CardContent sx={{ textAlign: "center", py: 6 }}>
							<Box sx={{ mb: 3 }}>
								<CircularProgress
									size={80}
									thickness={4}
									sx={{
										color: "primary.main",
										animation: "spin 1.5s linear infinite",
										"@keyframes spin": {
											from: { transform: "rotate(0deg)" },
											to: { transform: "rotate(360deg)" },
										},
									}}
								/>
							</Box>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 700,
									mb: 2,
									color: "text.primary",
								}}
							>
								Processing Payment
							</Typography>
							<Typography
								variant="body1"
								sx={{ color: "text.secondary", mb: 3 }}
							>
								Please wait while we securely process your
								payment...
							</Typography>
							<Chip
								icon={<MyShieldIcon />}
								label="256-bit SSL Encryption"
								variant="outlined"
								sx={{
									backgroundColor: "rgba(76, 175, 80, 0.1)",
									color: "#4caf50",
									borderColor: "rgba(76, 175, 80, 0.2)",
									fontWeight: 500,
								}}
							/>
						</CardContent>
					</Card>
				) : (
					<>
						<Grid item xs={12} md={5} className="my-3">
							<div className="px-5">
								<Card
									sx={{
										maxWidth: "500px",
										mx: "auto",
										borderRadius: "24px",
										background: "rgba(255, 255, 255, 0.95)",
										backdropFilter: "blur(10px)",
										boxShadow:
											"0 20px 60px rgba(0, 0, 0, 0.1)",
										border: "1px solid rgba(255, 255, 255, 0.2)",
										overflow: "hidden",
									}}
								>
									{/* Premium Header */}
									<Box
										sx={{
											background:
												"linear-gradient(135deg, #1976d2 0%, #3f51b5 50%, #9c27b0 100%)",
											p: 4,
											textAlign: "center",
											position: "relative",
											color: "white",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												top: 15,
												right: 15,
												background:
													"rgba(255, 255, 255, 0.2)",
												borderRadius: "50%",
												width: 40,
												height: 40,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<MyStarIcon
												color="gold"
												size={20}
											/>
										</Box>

										<Typography
											variant="h4"
											sx={{
												fontWeight: 700,
												mb: 1,
												color: "white",
											}}
										>
											Complete Your Purchase
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: "rgba(255, 255, 255, 0.8)",
											}}
										>
											Secure payment powered by
											industry-leading encryption
										</Typography>
									</Box>

									<CardContent sx={{ p: 4, pb: 2 }}>
										{/* Plan Header */}
										<Box
											sx={{ textAlign: "center", mb: 4 }}
										>
											<Box
												sx={{
													width: 80,
													height: 80,
													background: "linear-gradient(135deg, #1976d2, #3f51b5)",
													borderRadius: "50%",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													color: "white",
													mx: "auto",
													mb: 2,
												}}
											>
												<MyCreditCardIcon
													sx={{
														width: 30,
														height: 30,
														color: "white",
													}}
												/>
											</Box>
											<Typography
												variant="h4"
												sx={{
													fontWeight: 700,
													mb: 1,
													color: "text.primary",
												}}
											>
												{planData?.name}
											</Typography>
											<Box
												sx={{
													display: "flex",
													alignItems: "baseline",
													justifyContent: "center",
												}}
											>
												<Typography
													variant="h3"
													sx={{
														fontWeight: 700,
														color: "primary.main",
														mr: 0.5,
													}}
												>
													{planData?.currencyDetails?.symbol || "$"}{" "}
													{planData?.isCustomPlan
														? Number(planData?.amountToPay).toFixed(2)
														: planData?.amountToPay
															? Number(
																	Number(planData?.amountToPay) * Number(planData?.currencyDetails?.exchangeRate)
																).toFixed(2)
															: "0.00"}
												</Typography>
												<Typography
													variant="body1"
													sx={{
														color: "text.secondary",
													}}
												>
													/
													{planData?.billingCycle?.toLowerCase() || ""}
												</Typography>
											</Box>
											<Typography variant="p" sx={{ color: "text.secondary" }}>
												{planData?.currencyDetails?.symbol !== "$" && (
													<>
														${" "}
														{!planData?.isCustomPlan
															? Number(planData?.amountToPay).toFixed(2)
															: planData?.amountToPay
																? Number(
																		Number(planData?.amountToPay) / Number(planData?.currencyDetails?.exchangeRate)
																	).toFixed(2)
																: "0.00"}
													</>
												)}
											</Typography>
										</Box>

										<Divider sx={{ my: 3 }} />

										{/* Plan Details */}
										<Box sx={{ mb: 4 }}>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													p: 2,
													mb: 2,
													background:
														"linear-gradient(135deg, #f8fafc, #e3f2fd)",
													borderRadius: "16px",
													border: "1px solid rgba(25, 118, 210, 0.1)",
												}}
											>
												<Box
													sx={{
														width: 48,
														height: 48,
														background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
														borderRadius: "12px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														mr: 2,
														color: "primary.main",
													}}
												>
													<MyCalendarIcon size={20} />
												</Box>
												<Box>
													<Typography
														variant="caption"
														sx={{
															color: "text.secondary",
															display: "block",
														}}
													>
														Billing Cycle
													</Typography>
													<Typography variant="body1" sx={{ fontWeight: 600 }}>
														{planData?.billingCycle}
													</Typography>
												</Box>
											</Box>

											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													p: 2,
													background: "linear-gradient(135deg, #f1f8e9, #e8f5e8)",
													borderRadius: "16px",
													border: "1px solid rgba(76, 175, 80, 0.1)",
												}}
											>
												<Box
													sx={{
														width: 48,
														height: 48,
														background: "linear-gradient(135deg, #e8f5e8, #c8e6c9)",
														borderRadius: "12px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														mr: 2,
														color: "success.main",
													}}
												>
													<MyMoneyIcon size={20} />
												</Box>
												<Box>
													<Typography
														variant="caption"
														sx={{
															color: "text.secondary",
															display: "block",
														}}
													>
														Total Amount
													</Typography>
													<Typography variant="body1" sx={{ fontWeight: 600 }}>
														{planData?.currencyDetails?.symbol || "$"}{" "}
														{planData?.isCustomPlan
															? Number(planData?.amountToPay).toFixed(2)
															: planData?.amountToPay
																? Number(
																		Number(planData?.amountToPay) * Number(planData?.currencyDetails?.exchangeRate)
																	).toFixed(2)
																: "0.00"}
													</Typography>
                                                    {planData?.currencyDetails?.symbol !== "$" && (
                                                        <>
                                                            <Typography variant="body1" sx={{color: "text.secondary"}}>
                                                                ${" "}
                                                                {!planData?.isCustomPlan
                                                                    ? Number(planData?.amountToPay).toFixed(2)
                                                                    : planData?.amountToPay
                                                                        ? Number(
                                                                                Number(planData?.amountToPay) / Number(planData?.currencyDetails?.exchangeRate)
                                                                            ).toFixed(2)
                                                                        : "0.00"}
                                                            </Typography>
                                                        </>
                                                    )}
													
												</Box>
											</Box>
										</Box>

										{/* Features */}
										<Box sx={{ mb: 4 }}>
											<Typography
												variant="h6"
												sx={{
													fontWeight: 700,
													mb: 2,
													color: "text.primary",
												}}
											>
												What's included:
											</Typography>
											<Grid container spacing={1}>
												{planData?.features?.map(
													(item, index) => (
														<Grid
															item
															xs={12}
															key={index}
														>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																}}
															>
																<Box
																	sx={{
																		width: 24,
																		height: 24,
																		background: "linear-gradient(135deg, #4caf50, #66bb6a)",
																		borderRadius: "50%",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		color: "white",
																		mr: 2,
																		flexShrink: 0,
																	}}
																>
																	<MyCheckIcon size={ 12 } />
																</Box>
																<Typography
																	variant="body2"
																	sx={{
																		color: "text.primary",
																	}}
																>
																	{item?.feature || ""}{" "}-{" "}{item?.value || ""}
																</Typography>
															</Box>
														</Grid>
													),
												)}
											</Grid>
										</Box>

										{/* Footer Text */}
										<Typography
											variant="caption"
											sx={{
												color: "text.secondary",
												textAlign: "center",
												display: "block",
												mt: 2,
												lineHeight: 1.4,
											}}
										>
											By proceeding, you agree to our
											Terms of Service and Privacy Policy.
											You can cancel anytime from your
											account settings.
										</Typography>
									</CardContent>

									{/* Security Badges */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 3,
											flexWrap: "wrap",
											color: "text.secondary",
											fontSize: "0.875rem",
											my: 3,
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<LockIcon
												sx={{
													fontSize: 16,
													color: "success.main",
												}}
											/>
											<Typography variant="body2">
												256-bit SSL Encrypted
											</Typography>
										</Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<VerifiedIcon
												sx={{
													fontSize: 16,
													color: "success.main",
												}}
											/>
											<Typography variant="body2">
												PCI-DSS Compliant
											</Typography>
										</Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<ShieldIcon
												sx={{
													fontSize: 16,
													color: "success.main",
												}}
											/>
											<Typography variant="body2">
												Powered by Plug'n Pay
											</Typography>
										</Box>
									</Box>
								</Card>
							</div>
						</Grid>

						<Grid
							item
							xs={12}
							md={7}
							className="my-3"
							style={{ maxWidth: "700px" }}
						>
							<div className="px-5">
								<Box className="mb-6">
									{/* Visual Card Preview */}
									<Card
										sx={{
											background: getCardGradient(),
											color: "white",
											borderRadius: 3,
											boxShadow: 4,
											minHeight: 250,
											position: "relative",
											overflow: "hidden",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												inset: 0,
												background:
													"linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
												minHeight: 250,
											}}
										/>
										<CardContent
											sx={{
												p: 3,
												position: "relative",
												height: "100%",
												minHeight: 250,
												display: "flex",
												flexDirection: "column",
												justifyContent: "space-between",
											}}
										>
											<Box
												sx={{
													display: "flex",
													justifyContent:
														"space-between",
													alignItems: "flex-start",
												}}
											>
												<Box
													sx={{
														width: 48,
														height: 36,
														background:
															"linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
														borderRadius: 1,
														opacity: 0.9,
													}}
												/>
												{cardType &&
													getCardTypeDisplay() && (
														<Chip
															label={
																getCardTypeDisplay()
																	.name
															}
															size="small"
															sx={{
																bgcolor:
																	"rgba(255,255,255,0.2)",
																backdropFilter:
																	"blur(10px)",
																color: "white",
																fontWeight: 700,
																fontSize:
																	"10px",
															}}
														/>
													)}
											</Box>

											<Box>
												<Typography
													variant="h6"
													sx={{
														fontFamily: "monospace",
														letterSpacing: 2,
														mb: 3,
														fontSize: "1.5rem",
														color: "white",
													}}
												>
													{cardNumber ||
														"•••• •••• •••• ••••"}
												</Typography>
											</Box>

											<Box
												sx={{
													display: "flex",
													justifyContent:
														"space-between",
													alignItems: "flex-end",
												}}
											>
												<Box>
													<Typography
														variant="caption"
														sx={{
															opacity: 0.7,
															fontSize: "0.7rem",
														}}
													>
														CARD HOLDER
													</Typography>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 600,
															fontSize:
																"0.875rem",
														}}
													>
														{cardHolder ||
															"YOUR NAME"}
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="caption"
														sx={{
															opacity: 0.7,
															fontSize: "0.7rem",
														}}
													>
														EXPIRES
													</Typography>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 600,
															fontSize:
																"0.875rem",
														}}
													>
														{expiryDate || "MM/YY"}
													</Typography>
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Box>
								<Box sx={{ "& > * + *": { mt: 3 } }}>
									{/* Payment Form Card */}
									<Card
										sx={{
											border: "1px solid",
											borderColor: "divider",
											boxShadow: 2,
										}}
									>
										<CardContent sx={{ p: 4 }}>
											<Box
												sx={{ "& > * + *": { mt: 3 } }}
											>
												{/* Card Number */}
												<Box
													sx={{
														"& > * + *": { mt: 1 },
													}}
												>
													<Typography
														component="label"
														htmlFor="card-number"
														sx={{
															fontSize:
																"0.875rem",
															fontWeight: 600,
															display: "block",
															mb: 1,
														}}
													>
														Card Number
													</Typography>
													<Box
														sx={{
															position:
																"relative",
														}}
													>
														<TextField
															id="card-number"
															type="text"
															inputMode="numeric"
															value={cardNumber}
															onChange={
																handleCardNumberChange
															}
															onBlur={() => {
																const error =
																	validateCardNumber(
																		cardNumber,
																	);
																setErrors({
																	...errors,
																	cardNumber:
																		error,
																});
															}}
															placeholder="1234 5678 9012 3456"
															fullWidth
															error={
																!!errors.cardNumber
															}
															InputProps={{
																startAdornment:
																	(
																		<InputAdornment position="start">
																			<CreditCardIcon
																				sx={{
																					color: "text.secondary",
																				}}
																			/>
																		</InputAdornment>
																	),
																endAdornment:
																	cardType &&
																	getCardTypeDisplay() ? (
																		<InputAdornment position="end">
																			<Chip
																				label={
																					getCardTypeDisplay()
																						.name
																				}
																				size="small"
																				sx={{
																					height: 24,
																					fontSize:
																						"10px",
																					fontWeight: 700,
																					bgcolor:
																						getCardTypeDisplay()
																							.color,
																					color: "white",
																					"& .MuiChip-label":
																						{
																							px: 1,
																						},
																				}}
																			/>
																		</InputAdornment>
																	) : null,
															}}
														/>
													</Box>
													{errors.cardNumber && (
														<Typography
															variant="caption"
															sx={{
																color: "error.main",
																fontSize:
																	"0.75rem",
																display: "flex",
																alignItems:
																	"center",
																gap: 0.5,
															}}
														>
															<InfoIcon
																sx={{
																	fontSize: 12,
																}}
															/>
															{errors.cardNumber}
														</Typography>
													)}
													{cardType &&
														!errors.cardNumber &&
														cardNumber.replace(
															/\s/g,
															"",
														).length > 6 && (
															<Typography
																variant="caption"
																sx={{
																	color: "success.main",
																	fontSize:
																		"0.75rem",
																	display:
																		"flex",
																	alignItems:
																		"center",
																	gap: 0.5,
																}}
															>
																<CheckIcon
																	sx={{
																		fontSize: 12,
																	}}
																/>
																{
																	getCardTypeDisplay()
																		?.name
																}{" "}
																detected
															</Typography>
														)}
												</Box>

												{/* Card Holder Name */}
												<Box
													sx={{
														"& > * + *": { mt: 1 },
													}}
												>
													<Typography
														component="label"
														htmlFor="card-holder"
														sx={{
															fontSize:
																"0.875rem",
															fontWeight: 600,
															display: "block",
															mb: 1,
														}}
													>
														Card Holder Name
													</Typography>
													<TextField
														id="card-holder"
														type="text"
														value={cardHolder}
														onChange={(e) => {
															setCardHolder(
																e.target.value.toUpperCase(),
															);
															if (
																errors.cardHolder
															) {
																setErrors({
																	...errors,
																	cardHolder:
																		"",
																});
															}
														}}
														onBlur={() => {
															const error =
																validateCardHolder(
																	cardHolder,
																);
															setErrors({
																...errors,
																cardHolder:
																	error,
															});
														}}
														placeholder="JOHN DOE"
														fullWidth
														error={
															!!errors.cardHolder
														}
														inputProps={{
															style: {
																textTransform:
																	"uppercase",
															},
														}}
													/>
													{errors.cardHolder && (
														<Typography
															variant="caption"
															sx={{
																color: "error.main",
																fontSize:
																	"0.75rem",
																display: "flex",
																alignItems:
																	"center",
																gap: 0.5,
															}}
														>
															<InfoIcon
																sx={{
																	fontSize: 12,
																}}
															/>
															{errors.cardHolder}
														</Typography>
													)}
												</Box>

												{/* Expiry & CVV */}
												<Grid
													container
													spacing={2}
													sx={{ mt: 1 }}
												>
													<Grid item xs={6}>
														<Box
															sx={{
																"& > * + *": {
																	mt: 1,
																},
															}}
														>
															<Typography
																component="label"
																htmlFor="expiry"
																sx={{
																	fontSize:
																		"0.875rem",
																	fontWeight: 600,
																	display:
																		"block",
																	mb: 1,
																}}
															>
																Expiry Date
															</Typography>
															<TextField
																id="expiry"
																type="text"
																value={
																	expiryDate
																}
																onChange={(
																	e,
																) => {
																	const formatted =
																		formatExpiryDate(
																			e
																				.target
																				.value,
																		);
																	if (
																		formatted.replace(
																			/\D/g,
																			"",
																		)
																			.length <=
																		4
																	) {
																		setExpiryDate(
																			formatted,
																		);
																		if (
																			errors.expiryDate
																		) {
																			setErrors(
																				{
																					...errors,
																					expiryDate:
																						"",
																				},
																			);
																		}
																	}
																}}
																onBlur={() => {
																	const error =
																		validateExpiryDate(
																			expiryDate,
																		);
																	setErrors({
																		...errors,
																		expiryDate:
																			error,
																	});
																}}
																placeholder="MM/YY"
																fullWidth
																error={
																	!!errors.expiryDate
																}
															/>
															{errors.expiryDate && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "error.main",
																		fontSize:
																			"0.75rem",
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	<InfoIcon
																		sx={{
																			fontSize: 12,
																		}}
																	/>
																	{
																		errors.expiryDate
																	}
																</Typography>
															)}
														</Box>
													</Grid>
													<Grid item xs={6}>
														<Box
															sx={{
																"& > * + *": {
																	mt: 1,
																},
															}}
														>
															<Typography
																component="label"
																htmlFor="cvv"
																sx={{
																	fontSize:
																		"0.875rem",
																	fontWeight: 600,
																	display:
																		"block",
																	mb: 1,
																}}
															>
																CVV{" "}
																{cardType ===
																	"amex" &&
																	"(4 digits)"}
															</Typography>
															<TextField
																id="cvv"
																type={
																	showCvv
																		? "text"
																		: "password"
																}
																value={cvv}
																onChange={(
																	e,
																) => {
																	const value =
																		e.target.value.replace(
																			/\D/g,
																			"",
																		);
																	const maxLength =
																		cardType ===
																		"amex"
																			? 4
																			: 3;
																	if (
																		value.length <=
																		maxLength
																	) {
																		setCvv(
																			value,
																		);
																		if (
																			errors.cvv
																		) {
																			setErrors(
																				{
																					...errors,
																					cvv: "",
																				},
																			);
																		}
																	}
																}}
																onBlur={() => {
																	const error =
																		validateCVV(
																			cvv,
																		);
																	setErrors({
																		...errors,
																		cvv: error,
																	});
																}}
																placeholder={
																	cardType ===
																	"amex"
																		? "1234"
																		: "123"
																}
																inputProps={{
																	maxLength:
																		cardType ===
																		"amex"
																			? 4
																			: 3,
																}}
																fullWidth
																error={
																	!!errors.cvv
																}
																InputProps={{
																	endAdornment:
																		(
																			<InputAdornment position="end">
																				<IconButton
																					onClick={() =>
																						setShowCvv(
																							!showCvv,
																						)
																					}
																					edge="end"
																					tabIndex={
																						-1
																					}
																					size="small"
																				>
																					{showCvv ? (
																						<EyeOffIcon />
																					) : (
																						<EyeIcon />
																					)}
																				</IconButton>
																			</InputAdornment>
																		),
																}}
															/>
															{errors.cvv && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "error.main",
																		fontSize:
																			"0.75rem",
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	<InfoIcon
																		sx={{
																			fontSize: 12,
																		}}
																	/>
																	{errors.cvv}
																</Typography>
															)}
														</Box>
													</Grid>
												</Grid>

												{/* Zip Code & Address */}
												<Grid
													container
													spacing={2}
													sx={{ mt: 1 }}
												>
													<Grid item xs={6}>
														<Box
															sx={{
																"& > * + *": {
																	mt: 1,
																},
															}}
														>
															<Typography
																component="label"
																htmlFor="zipCode"
																sx={{
																	fontSize:
																		"0.875rem",
																	fontWeight: 600,
																	display:
																		"block",
																	mb: 1,
																}}
															>
																Zip Code
															</Typography>
															<TextField
																id="zipCode"
																type="text"
																value={zipCode}
																onChange={(
																	e,
																) => {
																	const value =
																		e.target.value.replace(
																			/\D/g,
																			"",
																		);
																	if (
																		value.length <=
																		6
																	) {
																		setZipCode(
																			value,
																		);
																		if (
																			errors.zipCode &&
																			value.length ===
																				5
																		) {
																			setErrors(
																				{
																					...errors,
																					zipCode:
																						"",
																				},
																			);
																		}
																	}
																}}
																onBlur={() => {
																	const error =
																		validateZipCode(
																			zipCode,
																		);
																	setErrors({
																		...errors,
																		zipCode:
																			error,
																	});
																}}
																placeholder="123456"
																fullWidth
																error={
																	!!errors.zipCode
																}
															/>
															{errors.zipCode && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "error.main",
																		fontSize:
																			"0.75rem",
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	<InfoIcon
																		sx={{
																			fontSize: 12,
																		}}
																	/>
																	{
																		errors.zipCode
																	}
																</Typography>
															)}
														</Box>
													</Grid>
													<Grid item xs={6}>
														<Box
															sx={{
																"& > * + *": {
																	mt: 1,
																},
															}}
														>
															<Typography
																component="label"
																htmlFor="address"
																sx={{
																	fontSize:
																		"0.875rem",
																	fontWeight: 600,
																	display:
																		"block",
																	mb: 1,
																}}
															>
																Address
															</Typography>
															<TextField
																id="address"
																type="text"
																value={address}
																onChange={(
																	e,
																) => {
																	setAddress(
																		e.target
																			.value,
																	);
																}}
																onBlur={() => {
																	const error =
																		validateAddress(
																			address,
																		);
																	setErrors({
																		...errors,
																		address:
																			error,
																	});
																}}
																placeholder="123 Main St"
																fullWidth
																error={
																	!!errors.address
																}
															/>
															{errors.address && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "error.main",
																		fontSize: "0.75rem",
																		display: "flex",
																		alignItems: "center",
																		gap: 0.5,
																	}}
																>
																	<InfoIcon
																		sx={{
																			fontSize: 12,
																		}}
																	/>
																	{
																		errors.address
																	}
																</Typography>
															)}
														</Box>
													</Grid>
												</Grid>

												<Box>
													{/* Payment Button */}
													<Button
														variant="contained"
														fullWidth
														size="large"
														onClick={handlePayment}
														startIcon={
															<MyCreditCardIcon />
														}
														sx={{
															py: 2,
															background: "linear-gradient(135deg, #1976d2, #3f51b5)",
															borderRadius: "16px",
															textTransform: "none",
															fontSize: "16px",
															fontWeight: 600,
															boxShadow: "0 8px 25px rgba(25, 118, 210, 0.3)",
															"&:hover": {
																background: "linear-gradient(135deg, #1565c0, #3949ab)",
																boxShadow: "0 12px 35px rgba(25, 118, 210, 0.4)",
																transform: "translateY(-2px)",
															},
															transition: "all 0.3s ease",
														}}
													>
														Pay{" "}
														{planData?.currencyDetails?.symbol || "$"}{" "}
														{planData?.isCustomPlan
															? Number(planData?.amountToPay).toFixed(2)
															: planData?.amountToPay
																? Number(
																		Number(planData?.amountToPay) * Number(planData?.currencyDetails?.exchangeRate),
																	).toFixed(2)
																: "0.00"}{" "}
                                                        {planData?.currencyDetails?.symbol !== "$" && (
                                                            <>
                                                                ${" "}
                                                                {!planData?.isCustomPlan
                                                                    ? `($ ${Number(planData?.amountToPay).toFixed(2)})`
                                                                    : planData?.amountToPay
                                                                        ? `($ ${Number(
                                                                                Number(planData?.amountToPay) /
                                                                                    Number(planData?.currencyDetails?.exchangeRate),
                                                                            ).toFixed(2)})`
                                                                        : `($ 0.00)`}{" "}
                                                            </>
                                                        )}
														Securely
													</Button>
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Box>
							</div>
						</Grid>
					</>
				)}
			</Grid>

			<CustomSnackbar
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={handleCloseSnackbar}
			/>
		</Box>
	);
};

export default PlanPaymentPage;
